// ============================================================
// ANSICHTSSACHE-AGENT – Registerleiste
// ============================================================
// EIN Bauteil fuer alle Register der Anwendung. Vorher trug jede
// Stelle ihre eigene Zeile mit eigenen Abstaenden und eigener
// Markierung — das faellt einzeln nicht auf und in der Summe
// sofort.
//
// Zwei Entscheidungen stecken darin:
//
//   Unterstrich statt Pillen. Die Register sind eine Ebene UNTER
//   dem Seitentitel und duerfen nicht mit den Filterpillen der
//   Listenseiten verwechselt werden. Der Unterstrich sagt
//   „Abschnitt dieser Seite“, die Pille sagt „Auswahl“.
//
//   Wenn KEIN Register passt, verschwindet die Leiste. Eine
//   Unterseite wie der Stundenauszug ist kein Register — eine
//   Leiste, in der nichts markiert ist, sieht kaputt aus und ist
//   es in gewisser Weise auch.
//
// Der Klick quittiert SOFORT (26.09.2026, „der Tab-Wechsel dauert
// mega lang"): der Unterstrich springt auf das angeklickte Register
// und pulsiert, bis der neue Inhalt da ist, und der alte Inhalt
// darunter tritt zurueck (`styles/globals.css`, „Registerwechsel").
// Vorher geschah nach dem Klick sichtbar NICHTS, bis der Server
// geantwortet hatte. Im Entwicklungsserver ist das die Regel und
// nicht die Ausnahme: Next laedt dort nichts vor, auch nicht das
// Ladegeruest (`createPrefetchURL` gibt im Dev `null` zurueck), und
// eine Seite, die seit der letzten Codeaenderung nicht aufgerufen
// wurde, wird erst beim Klick uebersetzt — gemessen 1,2 bis 2,2 s.
//
// Die Quelle ist `useLinkStatus` und kein eigener Zustand: sie
// kennt das Ende auch dann, wenn die Adresse gleich bleibt (Fehler,
// abgebrochener Wechsel), und kann deshalb nicht haengenbleiben.
//
// Zwei Lagen (seit 21.10.2026): als Zeile unter dem Seitenkopf
// (Vorgabe) oder als Spalte links neben dem Inhalt (`lage="spalte"`)
// — dieselben Register, dieselbe Wahl des aktiven, dieselbe Quittung.
// ============================================================

"use client";

import { useEffect, useRef } from "react";
import Link, { useLinkStatus } from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";

export type Register = {
  /** Was hinter der Basis steht. Leer = die Basis selbst. */
  schluessel: string;
  label: string;
  zaehler?: number;
};

export function RegisterLeiste({
  basis,
  register,
  className,
  lage = "zeile",
  vorgabe,
}: {
  /** Gemeinsamer Anfang aller Register-Adressen, ohne Schrägstrich am Ende. */
  basis: string;
  register: Register[];
  className?: string;
  /**
   * „zeile“: Tabs mit Unterstrich unter dem Seitenkopf. „spalte“:
   * dieselben Register untereinander in einer Karte links neben dem
   * Inhalt, in der Gestalt der Sammlungen im DAM und der Kapitel der
   * Marke (seit 21.10.2026, Projekt im Kundenbereich ab 1280px Breite).
   */
  lage?: "zeile" | "spalte";
  /**
   * Schluessel des Registers, das gilt, wenn keines passt — fuer
   * Unterseiten, die zu einem Register gehoeren (die Aufgabe im
   * Kundenbereich gehoert zur Uebersicht). Ohne Angabe verschwindet
   * die Leiste dort.
   */
  vorgabe?: string;
}) {
  const pathname = usePathname();
  const leiste = useRef<HTMLElement>(null);

  const mitPfad = register.map((r) => ({
    ...r,
    href: r.schluessel ? `${basis}/${r.schluessel}` : basis,
  }));

  // Das laengste passende Register gewinnt — sonst gewaenne die
  // Basis gegen alle, weil jeder Pfad mit ihr anfaengt.
  const aktiv =
    mitPfad
      .filter((r) => (r.schluessel ? pathname.startsWith(r.href) : pathname === basis))
      .sort((a, b) => b.href.length - a.href.length)[0] ??
    (vorgabe === undefined ? undefined : mitPfad.find((r) => r.schluessel === vorgabe));

  // Auf dem Telefon ist die Leiste breiter als das Bild und rollt
  // seitlich. Das gewaehlte Register steht dann womoeglich hinter
  // dem Rand — „Abwesenheiten“ in Mein Konto war nur zur Haelfte
  // zu sehen. Die Leiste rollt es deshalb ins Bild, und zwar nur
  // sich selbst: `scrollIntoView` rollte die ganze Seite mit.
  const aktivHref = aktiv?.href;
  useEffect(() => {
    const nav = leiste.current;
    const el = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !el || nav.scrollWidth <= nav.clientWidth) return;
    const links = el.offsetLeft - nav.offsetLeft;
    if (links < nav.scrollLeft || links + el.offsetWidth > nav.scrollLeft + nav.clientWidth) {
      nav.scrollLeft = Math.max(0, links - (nav.clientWidth - el.offsetWidth) / 2);
    }
  }, [aktivHref]);

  if (!aktiv) return null;

  if (lage === "spalte") {
    return (
      <nav
        aria-label="Abschnitte"
        data-registerleiste=""
        className={cn(
          "bg-bg-elevated shadow-e2 rounded-xl p-2 ring-1 ring-black/[0.04] dark:ring-white/[0.06]",
          className,
        )}
      >
        <ul className="space-y-0.5">
          {mitPfad.map((r) => {
            const istAktiv = r.href === aktiv.href;

            return (
              <li key={r.schluessel || "basis"}>
                <Link
                  href={r.href}
                  aria-current={istAktiv ? "page" : undefined}
                  className={cn(
                    "text-body-sm flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5",
                    "duration-fast transition-colors",
                    istAktiv
                      ? "bg-bg-tertiary text-text-primary font-medium"
                      : "text-text-secondary hover:bg-bg-secondary",
                    // Wie in der Zeile: das ladende Register steht
                    // sofort als das gewaehlte da und pulsiert, bis
                    // sein Inhalt kommt; das bisherige tritt zurueck.
                    "has-[[data-wartet]]:bg-bg-tertiary has-[[data-wartet]]:text-text-primary",
                    "has-[[data-wartet]]:animate-pulse motion-reduce:has-[[data-wartet]]:animate-none",
                    istAktiv &&
                      "[nav[data-registerleiste]:has([data-wartet])_&:not(:has([data-wartet]))]:text-text-secondary [nav[data-registerleiste]:has([data-wartet])_&:not(:has([data-wartet]))]:bg-transparent",
                  )}
                >
                  <span className="truncate">{r.label}</span>
                  {r.zaehler !== undefined && r.zaehler > 0 && (
                    <span className="numeric text-caption text-text-tertiary ml-auto shrink-0">
                      {r.zaehler}
                    </span>
                  )}
                  <Wartezeichen />
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    );
  }

  return (
    <nav
      ref={leiste}
      aria-label="Abschnitte"
      // Anker fuer „Registerwechsel" in `styles/globals.css`: was
      // hinter der Leiste steht, tritt zurueck, solange ein
      // Register laedt.
      data-registerleiste=""
      // `overflow-y-hidden` gehoert zwingend dazu: `overflow-x-auto`
      // allein macht auch die Senkrechte zu `auto`, und die Leiste
      // ist durch den herausragenden Unterstrich des aktiven
      // Registers genau EINEN Pixel hoeher als ihr Kasten. Auf
      // Rechnern mit stets sichtbaren Rollbalken (macOS so
      // eingestellt, Windows immer) stand deshalb rechts ein
      // grauer Balken, der nichts rollt.
      className={cn(
        "border-border -mb-px flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b",
        // Auf dem Finger-Geraet ohne Balken: dort rollt man mit dem
        // Daumen, und der Balken stuende nur unter der Linie.
        "[@media(pointer:coarse)]:[scrollbar-width:none]",
        className,
      )}
    >
      {mitPfad.map((r) => {
        const istAktiv = r.href === aktiv.href;

        return (
          <Link
            key={r.schluessel || "basis"}
            href={r.href}
            aria-current={istAktiv ? "page" : undefined}
            className={cn(
              "text-body-sm relative flex shrink-0 items-center gap-1.5 px-3 py-2",
              "duration-fast ease-standard transition-colors",
              istAktiv
                ? "text-text-primary font-medium"
                : "text-text-secondary hover:text-text-primary",
              // Das ladende Register zeigt sich schon als das
              // gewaehlte, das bisherige tritt zurueck.
              "has-[[data-wartet]]:text-text-primary",
              istAktiv &&
                "[nav[data-registerleiste]:has([data-wartet])_&:not(:has([data-wartet]))]:text-text-secondary",
            )}
          >
            {r.label}
            {r.zaehler !== undefined && r.zaehler > 0 && (
              <span className="numeric bg-bg-tertiary text-caption text-text-tertiary rounded-md px-1.5 py-0.5">
                {r.zaehler}
              </span>
            )}
            <Unterstrich istAktiv={istAktiv} />
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * Der Strich unter dem gewaehlten Register. Eigenes Bauteil, weil
 * `useLinkStatus` nur INNERHALB eines `<Link>` antwortet.
 *
 * `data-wartet` ist die eine Markierung, an der alles haengt: der
 * Strich des bisherigen Registers geht aus, dessen Schrift wird
 * grau, und der Inhalt unter der Leiste blasst ab.
 */
function Unterstrich({ istAktiv }: { istAktiv: boolean }) {
  const { pending } = useLinkStatus();

  return (
    <span
      aria-hidden
      data-wartet={pending ? "" : undefined}
      className={cn(
        "bg-accent absolute inset-x-2 -bottom-px h-0.5 rounded-full",
        "duration-fast transition-opacity",
        istAktiv || pending ? "opacity-100" : "opacity-0",
        pending && "animate-pulse motion-reduce:animate-none",
        "[nav[data-registerleiste]:has([data-wartet])_&:not([data-wartet])]:opacity-0",
      )}
    />
  );
}

/**
 * Dieselbe Markierung fuer die Spalte, ohne eigenes Bild: dort
 * zeigt der Grund des Eintrags, was gewaehlt ist und was laedt.
 */
function Wartezeichen() {
  const { pending } = useLinkStatus();
  return <span aria-hidden hidden data-wartet={pending ? "" : undefined} />;
}
